import { useMemo } from "react";

import { download, getExcelTemplateName } from "src/utils/page";
import { getDataSourceStore } from "src/utils/devextreme-grid";
import axios from "axios";
import api, { useSWR } from "../utils/axios";

import { HOST_API } from "../config-global";

export const endpoint = "users";

const baseUrl = `/api/${endpoint}/`;

export function me() {
  return api.get(`${baseUrl}me`);
}

export function login(data) {
  return axios.post(`${HOST_API}${baseUrl}login`, data);
}

export function logout(data) {
  return axios.post(`${HOST_API}${baseUrl}logout`, data);
}

export function loginEnterprise(data) {
  return axios.post(`${HOST_API}${baseUrl}login/enterprise`, data);
}

export function requestMfaLogin(data) {
  return axios.post(`${HOST_API}${baseUrl}login/mfa/request`, data);
}

export function loginMfa(data) {
  return axios.post(`${HOST_API}${baseUrl}login/mfa/complete`, data);
}

export function refreshToken() {
  return axios.post(`${HOST_API}${baseUrl}refresh-token`, {
    accessToken: window.localStorage.getItem("accessToken"),
    refreshToken: window.localStorage.getItem("refreshToken"),
  });
}

export function refreshAccessToken() {
  return axios.post(`${HOST_API}${baseUrl}refresh-access-token`, {
    accessToken: window.localStorage.getItem("accessToken"),
  });
}

export function loginSocialNetwork(data) {
  return axios.post(`${HOST_API}${baseUrl}login-social-network`, data);
}

export function createOneTimePassword(data) {
  return api.post(`${baseUrl}one-time-passwords`, data);
}

export function removeOneTimePassword(id) {
  return api.delete(`${baseUrl}one-time-passwords/${id}`);
}

export function getGridFilter(filters) {
  return getDataSourceStore(`${HOST_API}${baseUrl}get-grid-list`, {}, filters);
}

export function useGetOneTimePasswords() {
  const url = `${baseUrl}one-time-passwords`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(url);

  const memoizedValue = useMemo(
    () => ({
      passwords: data ?? [],
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function forgotPassword(email) {
  return axios.post(`${HOST_API}${baseUrl}forgot-password`, { email });
}

export function newPassword(data) {
  return axios.put(`${HOST_API}${baseUrl}new-password`, data);
}

export function startResetPassword() {
  return api.put(`${HOST_API}${baseUrl}password/reset/start`);
}

export function validateOtpToResetPassword(data) {
  return api.put(`${HOST_API}${baseUrl}password/reset/validate`, data);
}

export function resetPassword(data) {
  return api.put(`${HOST_API}${baseUrl}password/reset/complete`, data);
}

export function requestMfaToResetPassword(data) {
  return api.put(`${HOST_API}${baseUrl}password/reset/request`, data);
}

export function linkSocialNetworkToNewAccount(data) {
  return axios.put(`${HOST_API}${baseUrl}link-social-network-new-account`, data);
}

export function linkSocialNetwork(data) {
  return api.put(`${HOST_API}${baseUrl}link-social-network`, data);
}

export async function downloadExcelTemplate() {
  const response = await api.get(`${baseUrl}download-excel-template/`, { responseType: "blob" });

  return download(response, getExcelTemplateName("users"));
}

export function unlinkSocialNetwork(data) {
  return api.put(`${HOST_API}${baseUrl}unlink-social-network`, data);
}

export function getLinkedSocialNetworks() {
  return api.get(`${HOST_API}${baseUrl}get-linked-social-networks`);
}

export function useGetUserNotifications() {
  const url = `${HOST_API}${baseUrl}get-user-notifications`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(url);

  const memoizedValue = useMemo(
    () => ({
      notifications: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function setUserNotification(notificationId, enable, discriminator) {
  return api.put(`${HOST_API}${baseUrl}set-user-notification`, {
    notificationId,
    enable,
    discriminator,
  });
}

export function setAllUserNotification(enable, discriminator) {
  return api.put(`${HOST_API}${baseUrl}set-all-user-notification`, {
    enable,
    discriminator,
  });
}

export function updateProfile(data) {
  return api.put(`${HOST_API}${baseUrl}update-profile`, data);
}

export function getProfileSignature() {
  return api.get(`${baseUrl}profile-signature`);
}

export function updateProfileSignature(data) {
  return api.put(`${baseUrl}update-profile-signature`, data);
}

export function getGrid() {
  return getDataSourceStore(`${HOST_API}${baseUrl}get-grid`);
}

export function getList(search, type, locationId, providerId) {
  return api.post(`${baseUrl}get-list`, {
    search,
    type,
    locationId,
    providerId,
  });
}

export function remove(id) {
  return api.delete(`${baseUrl}${id}`);
}

export function create(data) {
  return api.post(`${baseUrl}`, data);
}

// El alta pública de usuarios Claims la usa alguien que todavía no tiene cuenta, así que va por
// axios directo como el resto del circuito anónimo (login, forgot-password). La instancia `api`
// no sirve acá: su interceptor cancela el request cuando no hay token en el localStorage.
export function createClaims(data) {
  return axios.post(`${HOST_API}${baseUrl}create-claims`, data);
}

export function getCanCreateClaims(enterpriseName) {
  return axios.get(`${HOST_API}${baseUrl}can-create-claims/${encodeURIComponent(enterpriseName)}`);
}

export function update(id, data) {
  return api.put(`${baseUrl}update/${id}`, { ...data });
}

export function getById(id) {
  return api.get(`${baseUrl}${id}`);
}

export function getLicense() {
  return api.post(`${baseUrl}get-license`, {});
}

export function useGetUser(id, undelete = false) {
  const url = `${baseUrl}${undelete ? "get-undelete/" : ""}${id}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(id ? url : null);

  const memoizedValue = useMemo(
    () => ({
      user: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetUserDetail(id) {
  const url = `${baseUrl}${id}/detail`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(url);

  const memoizedValue = useMemo(
    () => ({
      user: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetAtachments(id) {
  const url = `${baseUrl}${id}/attachments/gallery`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(id ? url : null);

  const memoizedValue = useMemo(
    () => ({
      user: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function startEmailChange() {
  return api.put(`${HOST_API}${baseUrl}email-change/start`);
}

export function validateOtpToEmailChange(data) {
  return api.put(`${HOST_API}${baseUrl}email-change/validate`, data);
}

export function requestMfaToEmailChange(data) {
  return api.put(`${HOST_API}${baseUrl}email-change/request`, data);
}

export function requestEmailChange(data) {
  return api.post(`${baseUrl}email-change/request-change`, data);
}

export function useGetMfaSettings() {
  const url = `${HOST_API}${baseUrl}mfa/settings`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(url);

  const memoizedValue = useMemo(
    () => ({
      mfaSettings: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function getMfaSettings() {
  return api.get(`${HOST_API}${baseUrl}mfa/settings`);
}

export function setupEmailMfa() {
  return api.post(`${HOST_API}${baseUrl}mfa/email/setup`);
}

export function finishSetupEmailMfa(data) {
  return api.post(`${HOST_API}${baseUrl}mfa/email/finish`, data);
}

export function startRemoveEmailMfa() {
  return api.post(`${HOST_API}${baseUrl}mfa/email/remove/start`);
}

export function requestMfaToRemove(data) {
  return api.post(`${HOST_API}${baseUrl}mfa/remove/request`, data);
}

export function finishRemoveEmailMfa(data) {
  return api.post(`${HOST_API}${baseUrl}mfa/email/remove/complete`, data);
}

export function confirmEmailChange(data) {
  return axios.put(`${HOST_API}${baseUrl}email-change/confirm`, data);
}

export function cancelEmailChange(data) {
  return axios.put(`${HOST_API}${baseUrl}email-change/cancel`, data);
}
